import { Suspense, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, LayoutGrid, SlidersHorizontal } from 'lucide-react';
import { Navigate, Route, Routes, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { AppShell, useShellChrome } from '@/components/layout/AppShell';
import { Sidebar, type ContextEntry, type NavGroup } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { can } from '@/config/access';
import {
  BRAND_HUB_ROUTES,
  CAMPAIGN_SECTION_ROUTES,
  DEFAULT_ROUTE,
  NAV_GROUPS,
  UNLISTED_ROUTES,
  WORKSPACE_ROUTES,
  type AppRoute,
} from '@/config/routes';
import { signOut, useActiveMembership, useSession } from '@/features/auth/session';
import { useLiveMembership } from '@/features/auth/useLiveMembership';
import { BRAND_TAB_ICONS, BRAND_TABS } from '@/features/brandHub/brand/brandTabs';
import { brandTabPath } from '@/features/brandHub/paths';
import { useBrand } from '@/features/brandHub/brand/useBrands';
import { useBrandProductCount } from '@/features/brandHub/products/useProducts';
import { useCampaign } from '@/features/campaigns/useCampaigns';
import { usePageHeading } from '@/features/layout/usePageHeading';
import {
  useApplyBrandColors,
  useOrganization,
  useWatchOrganizationBranding,
} from '@/features/organization/useOrganization';
import { useTheme } from '@/hooks/useTheme';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { activeLanguage, LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '@/i18n';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  name: LANGUAGE_NAMES[code],
}));

const GROUP_ICONS: Record<string, JSX.Element> = {
  brandHub: <BookOpen size={16} />,
  workspace: <LayoutGrid size={16} />,
};

export function App() {
  const session = useSession();
  const { pathname } = useLocation();

  if (!session) return <LoginPage />;

  if (matchPath('/login', pathname)) return <Navigate to={DEFAULT_ROUTE} replace />;

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { t, i18n: instance } = useTranslation();
  const language = activeLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const session = useSession();
  const membership = useActiveMembership();
  const { theme, setTheme } = useTheme();
  const heading = usePageHeading();
  const { collapsed, toggleCollapsed, drawerOpen, openDrawer, closeDrawer } = useShellChrome();

  const organization = useOrganization();
  useWatchOrganizationBranding();
  useApplyBrandColors(
    organization.data?.primaryColor,
    organization.data?.secondaryColor,
  );

  useLiveMembership();

  const [openGroups, setOpenGroups] = useLocalStorage<Record<string, boolean>>(
    'edify_nav_groups',
    { brandHub: true, workspace: true },
  );

  const campaignMatch = matchPath('/campaigns/:campaignId/*', pathname);
  const campaignId = campaignMatch?.params.campaignId;
  const { campaign } = useCampaign(campaignId);

  const brandMatch = matchPath('/brand-hub/brand/:brandSlug/*', pathname);
  const brandSlug = brandMatch?.params.brandSlug === 'new' ? '' : (brandMatch?.params.brandSlug ?? '');
  const { brand } = useBrand(brandSlug);
  const productCount = useBrandProductCount(brandSlug || null);

  const allowed = (route: AppRoute) => can(membership, route.action);

  const groups: NavGroup[] = NAV_GROUPS.map((group) => ({
    key: group.key,
    label: t(group.labelKey),
    icon: GROUP_ICONS[group.key],
    open: openGroups[group.key] ?? true,
    onToggle: () =>
      setOpenGroups((current) => ({ ...current, [group.key]: !(current[group.key] ?? true) })),
    items: group.routes.filter(allowed).map((route) => ({
      to: route.path,
      label: t(route.labelKey),
      icon: route.icon,
      end: route.end,
    })),
  })).filter((group) => group.items.length > 0);

  const contextEntries: ContextEntry[] = CAMPAIGN_SECTION_ROUTES.flatMap((route) => {
    const item = {
      to: `/campaigns/${campaignId}/${route.path}`,
      label: t(route.labelKey),
      icon: route.icon,
    };
    return route.groupLabelKey
      ? [
          {
            label: t(route.groupLabelKey),
            icon: <SlidersHorizontal size={14} />,
            isLabel: true as const,
          },
          item,
        ]
      : [item];
  });

  const brandEntries: ContextEntry[] = BRAND_TABS.map((tab) => ({
    to: brandTabPath(brandSlug, tab),
    icon: createElement(BRAND_TAB_ICONS[tab], { size: 14 }),
    label:
      tab === 'products'
        ? `${t('brandHub.brand.tabs.products')} (${productCount})`
        : t(`brandHub.brand.tabs.${tab}`),
  }));

  const context =
    campaignId && campaign
      ? {
          groupKey: 'workspace',
          kindLabel: t('campaign.kind'),
          name: campaign.name,
          backTo: '/campaigns',
          backLabel: t('campaigns.allCampaigns'),
          entries: contextEntries,
        }
      : brandSlug && brand
        ? {
            groupKey: 'brandHub',
            kindLabel: t('brandHub.brand.title'),
            name: brand.name,
            backTo: '/brand-hub',
            backLabel: t('nav.brands'),
            entries: brandEntries,
          }
        : undefined;

  return (
    <AppShell
      collapsed={collapsed}
      heading={heading}
      sidebar={
        <Sidebar
          groups={groups}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          drawerOpen={drawerOpen}
          onCloseDrawer={closeDrawer}
          ariaLabel={t('app.name')}
          labels={{ collapse: t('common.collapseSidebar'), expand: t('common.expandSidebar') }}
          context={context}
        />
      }
      topBar={
        <TopBar
          onOpenDrawer={openDrawer}
          drawerOpen={drawerOpen}
          theme={theme}
          onThemeChange={setTheme}
          language={language}
          languages={LANGUAGE_OPTIONS}
          onLanguageChange={(code) => void instance.changeLanguage(code)}
          user={{
            name: session?.name ?? '',
            email: session?.email ?? '',
            roleLabel: t(`settings.role.${membership?.role ?? 'member'}`),
          }}
          isAdmin={membership?.role === 'admin'}
          onOpenProfile={() => navigate('/settings?tab=profile')}
          onOpenOrganization={() => navigate('/settings?tab=organization')}
          onSignOut={() => signOut()}
          labels={{
            openMenu: t('common.openMenu'),
            language: t('common.language'),
            theme: t('common.theme'),
            themeLight: t('common.themeLight'),
            themeDark: t('common.themeDark'),
            themeSystem: t('common.themeSystem'),
            profileMenu: t('common.profileMenu'),
            profile: t('nav.profile'),
            organization: t('nav.organization'),
            signOut: t('common.signOut'),
          }}
        />
      }
    >
      <Suspense fallback={<Skeleton className="h-40 rounded-xl" />}>
        <Routes>
        <Route path="/" element={<Navigate to={DEFAULT_ROUTE} replace />} />

        {[...WORKSPACE_ROUTES, ...BRAND_HUB_ROUTES, ...UNLISTED_ROUTES]
          .filter(allowed)
          .map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

        {CAMPAIGN_SECTION_ROUTES.map((route) => (
          <Route
            key={route.path}
            path={`/campaigns/:campaignId/${route.path}`}
            element={route.element}
          />
        ))}

        <Route
          path="/campaigns/:campaignId"
          element={<Navigate to="overview" replace relative="path" />}
        />

        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
